import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { cn } from "@/lib/utils"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Mail, Loader2 } from "lucide-react"
import { usePostAuthInvite } from "openapi/api/endpoints"
import { toast } from "sonner"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { CloudflareTurnstileGate } from "@/components/cloudflare-turnstile-gate"
import { isTurnstileRequired, TURNSTILE_INVITE_ACTION } from "@/lib/turnstile"



export const InviteUserSchema = z.object({
    email: z
      .email("Invalid email address"),
      role: z.enum(["Admin", "User"]),
  })

type InviteUserFormValues = z.infer<typeof InviteUserSchema>

type InviteUserDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const {mutateAsync:inviteUser,isPending}=usePostAuthInvite()
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const turnstileRequired = isTurnstileRequired()
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InviteUserFormValues>({
    resolver: zodResolver(InviteUserSchema),
    defaultValues: {
      email: "",
      role: "User",
    },
  })

  const role = watch("role")

  useEffect(() => {
    if (open) return

    setTurnstileToken(null)
    setTurnstileResetKey((key) => key + 1)
  }, [open])

  const onSubmit = async (data: InviteUserFormValues) => {
    try{

        await inviteUser({data:{
            email:data.email.toLowerCase(),
            role:data.role.toLowerCase() as 'admin' | 'user',
            turnstileToken: turnstileToken ?? undefined,
        }})
        reset()
        setTurnstileToken(null)
        setTurnstileResetKey((key) => key + 1)
        onOpenChange(false)
        toast.success("Invitation sent!",{
            description: `${data.email} has been invited to team`,
            action: {
              label: "Dismiss",
              onClick: () => {},
            },
            style: {
              background: '#a3e635'
            }
          })
    }catch{
        setTurnstileToken(null)
        setTurnstileResetKey((key) => key + 1)
        toast.error("An error occured!",{
            description:"Failed to invite user; please try again",
            action: {
              label: "Dismiss",
              onClick: () => {},
            },
            style: {
              background: "#dc2626",
            },
          });
    }

  }

  const handleClose = () => {
    reset()
    setTurnstileToken(null)
    setTurnstileResetKey((key) => key + 1)
    onOpenChange(false)
  }

  const formId = "invite-user-form"

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={<Mail className="size-4" />}
      title="Invite Team Member"
      description="Send an invitation to join your organization. They will receive an email with instructions."
      footer={
        <>
          <QuietAction size="md" onClick={handleClose} disabled={isPending}>
            Cancel
          </QuietAction>
          {/*
            The submit sits in the footer band while the form is in the body, so
            it is tied back by `form` rather than by being inside the <form>.
            Keeping it a real submit is what lets Enter in the email field send
            the invitation.
          */}
          <PageAction tone="primary"
            type="submit"
            form={formId}
            disabled={isPending || (turnstileRequired && !turnstileToken)}
          >
            {isPending && <Loader2 className="size-3.5 animate-spin" />}
            {isPending ? "Sending…" : "Send Invitation"}
          </PageAction>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <FieldShell label="Email address">
          <input
            id="email"
            type="email"
            aria-label="Email address"
            className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
            placeholder="john.doe@company.com"
          />
          {errors.email && (
            <p className="mt-1.5 text-[11px] leading-[1.4] text-destructive">
              {errors.email.message}
            </p>
          )}
        </FieldShell>

        <FieldShell label="Role" className="mt-5">
          <Select
            value={role}
            onValueChange={(value) => setValue("role", value as InviteUserFormValues["role"])}
          >
            <SelectTrigger
              id="role"
              aria-label="Role"
              className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
            >
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Admin">
                <span className="flex flex-col items-start">
                  <span className="text-[12.5px] font-medium">Admin</span>
                  <span className="text-[11px] text-muted-foreground">
                    Full access to all features and settings
                  </span>
                </span>
              </SelectItem>
              <SelectItem value="User">
                <span className="flex flex-col items-start">
                  <span className="text-[12.5px] font-medium">Recruiter</span>
                  <span className="text-[11px] text-muted-foreground">
                    Access to jobs, candidates, and outreach
                  </span>
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </FieldShell>

        <div className="mt-5" />
        <CloudflareTurnstileGate
          action={TURNSTILE_INVITE_ACTION}
          resetKey={turnstileResetKey}
          onTokenChange={setTurnstileToken}
        />
      </form>
    </DialogShell>
  )
}
